import { FC, useContext, useEffect, useState } from 'react'
import { Base64File, Base64FileInput, Button, SelectInput } from '@components/Generic'
import { observer } from 'mobx-react'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@methods/axiosConfig'
import { devLog } from '@methods/devLog'
import { forceRedirect } from '@methods/forceRedirect'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'
import { residenceProofType } from '@methods/getOnboardingContext'
import { API_URL } from '@constants/apiUrl'
import { SelectOption } from '@components/Generic/SelectInput/SelectInput'
import { cx } from '@/utils/cx'
import { components, DropdownIndicatorProps, GroupBase } from 'react-select'
import chevronDown from '@assets/amex-great/chevron-down.svg'
import uploadFileIcon from '@assets/amex-great/upload-file.svg'
import pageIcon from '@assets/amex-great/page.svg'
import deleteIcon from '@assets/amex-great/delete.svg'

interface ResidenceProofRetryScenarioAmexGreatProps {
  setLoading: (loading: boolean) => void
}

const DropdownIndicator = (
  props: DropdownIndicatorProps<SelectOption, boolean, GroupBase<SelectOption>>
) => (
  <components.DropdownIndicator {...props}>
    <img className="amex-great__select-chevron" src={chevronDown} alt="" width={24} height={24} />
  </components.DropdownIndicator>
)

export const ResidenceProofRetryScenarioAmexGreat: FC<ResidenceProofRetryScenarioAmexGreatProps> =
  observer(({ setLoading }) => {
    const store = useContext(StoreContext)
    const { context } = store.ScenarioState
    const { language } = store.TranslationsState
    const { pixelSpeak: trans, amexGreatPage: amexTrans } = store.TranslationsState.translations

    const [fileSelected, setFileSelected] = useState<Base64File>()
    const [fileError, setFileError] = useState('')
    const [fileInputKey, setFileInputKey] = useState(Date.now())
    const [isFileBeingDragged, setIsFileBeingDragged] = useState(false)

    const [isDisabled, setIsDisabled] = useState(true)
    const [utilityBillType, setUtilityBillType] = useState<SelectOption | undefined>(undefined)
    const [utilityBillTypeId, setUtilityBillTypeId] = useState(0)

    const allDocumentTypes: Record<string, string> = {
      ...amexTrans.addressVerification.documentTypes,
    }
    const documentTypesOptions = Object.keys(allDocumentTypes)
      .filter((item) => context?.availableResidenceProofTypes?.includes(item as residenceProofType))
      .filter((key) => key !== context?.previousAttempt?.residenceProofType)
      .map((key) => ({
        value: key,
        label: allDocumentTypes[key],
      }))

    useEffect(() => {
      setUtilityBillType(documentTypesOptions[utilityBillTypeId])
    }, [language])

    const resetFileInput = () => {
      setFileInputKey(Date.now())
      setFileSelected(undefined)
      setFileError('')
      setIsDisabled(true)
      setIsFileBeingDragged(false)
    }

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsFileBeingDragged(true)
    }

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsFileBeingDragged(false)
    }

    useEffect(() => {
      document.addEventListener('dragover', handleDragEnter, false)
      document.addEventListener('dragleave', handleDragLeave, false)

      return () => {
        document.removeEventListener('dragover', handleDragEnter, false)
        document.removeEventListener('dragleave', handleDragLeave, false)
      }
    })

    const handleProceed = async () => {
      if (isDisabled) {
        setFileError(trans.fileError)
      } else {
        setLoading(true)
        await handleFile()

        forceRedirect(`${API_URL}/pixel-speak/complete`)
      }
    }

    const getFiles = async (file: Base64File) => {
      setFileSelected(file)
    }

    const handleFile = async () => {
      if (!context?.documentUploadUrl) return

      try {
        await axiosInstance.post(
          context.documentUploadUrl,
          {
            file: fileSelected?.base64,
            type: utilityBillType?.value,
            fileName: fileSelected?.name,
          },
          {
            withCredentials: true,
          }
        )
      } catch (e) {
        devLog(e)
      }
    }

    useEffect(() => {
      setIsDisabled(true)
      if (fileSelected?.base64) {
        if (
          fileSelected.type !== 'application/pdf' &&
          fileSelected.type !== 'image/jpeg' &&
          fileSelected.type !== 'image/png'
        ) {
          setFileError(trans.fileErrorType)
        } else if (fileSelected.sizeMB >= 5) {
          setFileError(trans.fileErrorSize)
        } else {
          setFileError('')
          setIsDisabled(false)
        }
      } else {
        resetFileInput()
      }
    }, [fileSelected, fileSelected?.base64])

    const selectChangeHandle = (e: SelectOption) => {
      const index = documentTypesOptions.findIndex((item) => item.value === e.value)
      setUtilityBillType(e)
      setUtilityBillTypeId(index)
    }

    // The file name is shown in its own box, between the two halves of the sentence.
    const [beforeFileName, afterFileName] =
      trans.addressDescriptionResidenceProofRetry?.split('{fileName}') ?? []

    return (
      <>
        <div className="amex-great__section">
          <h2 className="amex-great__heading">{trans.headerResidenceProofRetry}</h2>

          <div className="amex-great__field">
            <p className="amex-great__label">{beforeFileName}</p>
            <div className="amex-great__readonly">{context?.previousAttempt?.documentFileName}</div>
          </div>

          <div className="amex-great__field">
            <p className="amex-great__label">{afterFileName}</p>
            <div className="amex-great__readonly amex-great__readonly--tall">
              {context?.expectedAddress?.street}, {context?.expectedAddress?.postalCode}{' '}
              {context?.expectedAddress?.city}
            </div>
          </div>

          <p className="amex-great__paragraph">{trans.descriptionResidenceProofRetry}</p>

          <div className="amex-great__document">
            <div className="amex-great__field">
              <p className="amex-great__label">{amexTrans.addressVerification.document}</p>
              <SelectInput
                options={documentTypesOptions}
                value={documentTypesOptions[utilityBillTypeId]}
                components={{ DropdownIndicator }}
                onChange={(e) => e && !Array.isArray(e) && selectChangeHandle(e as SelectOption)}
              />
            </div>

            <div
              className={cx(
                'amex-great__upload',
                fileSelected && 'amex-great__upload--has-file',
                isFileBeingDragged && 'amex-great__upload--dragging'
              )}
              tabIndex={0}
              role="button"
              onKeyDown={(event) =>
                onKeyDownHandler(event, () => {
                  document.getElementById('base64FileInput')?.click()
                })
              }
            >
              <Base64FileInput key={fileInputKey} multiple={false} onChange={getFiles} />
              {fileSelected ? (
                <div className="amex-great__file">
                  <img src={pageIcon} alt="" width={20} height={20} />
                  <div className="amex-great__file-details">
                    <span className="amex-great__file-name">{fileSelected.name}</span>
                    <span className="amex-great__file-size">
                      {fileSelected.sizeMB.toFixed(1)} MB
                    </span>
                  </div>
                  <span className="amex-great__file-action" onClick={() => resetFileInput()}>
                    <img src={deleteIcon} alt="" width={20} height={20} />
                  </span>
                </div>
              ) : (
                <>
                  <img src={uploadFileIcon} alt="" width={60} height={60} />
                  <div className="amex-great__upload-text">
                    {isFileBeingDragged ? (
                      <p className="amex-great__upload-title">{trans.dropHere}</p>
                    ) : (
                      <>
                        <p className="amex-great__upload-title">
                          <span className="amex-great__upload-link">
                            {amexTrans.addressVerification.clickHereToUpload}
                          </span>{' '}
                          {amexTrans.addressVerification.orDragAndDropYourFiles}
                        </p>
                        <p className="amex-great__upload-hint">
                          {amexTrans.addressVerification.maxFileSize}
                        </p>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {fileError && <p className="amex-great__error">{fileError}</p>}
          </div>
        </div>

        <div className="amex-great__actions amex-great__actions--end">
          <Button onClick={() => handleProceed()} disabled={isDisabled}>
            <span className="generic__button-text">{trans.proceed}</span>
          </Button>
        </div>
      </>
    )
  })
